import FileSaver from "file-saver";
import { BaseApplicationService } from "../../../domain/base/base-application-service";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { reportAsyncError } from "../../../../error-reporter";
import { AuthAuditLogView } from "../../../domain/audit/auth-audit-log";
import { SettingR } from "../../../../components/pages/settings/route";
import { toTotalPage } from "../../../domain/pagination/paging";
import { formatDate } from "../../../../utils/date-utils";

const checkExportStatusInterval = 3000;

export type AuthAuditLogContextProps = {
  isLoading: boolean;
  auditLogs: Array<AuthAuditLogView>;
  currentPage: number;
  totalPage: number;
  fromLimit: Date | undefined;
  dateRange: { from?: Date; to?: Date };
  setFilterCondition: typeof AuthAuditService.prototype.setFilterCondition;
  pushHistory: typeof AuthAuditService.prototype.pushHistory;
  replaceHistory: typeof AuthAuditService.prototype.replaceHistory;
  refreshAuditLogs: typeof AuthAuditService.prototype.refreshAuditLogs;
  startExportAndDownloadAuthAuditLogs: typeof AuthAuditService.prototype.exportAndDownloadAuthAuditLogs;
};

export class AuthAuditService extends BaseApplicationService<AuthAuditLogContextProps> {
  state = {
    isLoading: false,
    auditLogs: [],
    currentPage: 1,
    totalPage: 1,
    fromLimit: undefined,
    auditLogFilter: {},
    dateRange: {
      from: undefined,
      to: undefined,
    },
    setFilterCondition: this.setFilterCondition.bind(this),
    pushHistory: this.pushHistory.bind(this),
    replaceHistory: this.replaceHistory.bind(this),
    refreshAuditLogs: this.refreshAuditLogs.bind(this),
    startExportAndDownloadAuthAuditLogs:
      this.exportAndDownloadAuthAuditLogs.bind(this),
  };

  setFilterCondition(filter: {
    page: number;
    dateRange: { from?: Date; to?: Date };
  }) {
    this.setState({
      currentPage: filter.page,
      dateRange: filter.dateRange,
    });
  }

  pushHistory() {
    this.history.push(
      SettingR.AuthAudit.link({
        page: this.state.currentPage.toString(),
        from: this.state.dateRange.from
          ? formatDate(this.state.dateRange.from)
          : undefined,
        to: this.state.dateRange.to
          ? formatDate(this.state.dateRange.to)
          : undefined,
      }),
    );
  }

  replaceHistory() {
    this.history.replace(
      SettingR.AuthAudit.link({
        page: this.state.currentPage.toString(),
        from: this.state.dateRange.from
          ? formatDate(this.state.dateRange.from)
          : undefined,
        to: this.state.dateRange.to
          ? formatDate(this.state.dateRange.to)
          : undefined,
      }),
    );
  }

  async refreshAuditLogs() {
    this.setState({ isLoading: true });
    try {
      const { data, headers } = await apiClient.fetchAuditLogs({
        ...this.state.auditLogFilter,
        ...this.state.dateRange,
        page: this.state.currentPage,
      });
      this.setState({
        currentPage: this.state.currentPage,
        totalPage: toTotalPage(Number(headers["pagination-count"])),
        fromLimit: headers["x-from-limit"]
          ? new Date(headers["x-from-limit"])
          : undefined,
        auditLogs: data,
      });
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return;
      }
      reportAsyncError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async exportAndDownloadAuthAuditLogs(
    onExportSuccess: () => void,
    onExportFailed: (e: Error) => void,
  ) {
    try {
      const { data } = await apiClient.startExportAuthAuditLogs({
        ...this.state.auditLogFilter,
        ...this.state.dateRange,
      });

      const taskID = data.taskID;

      const intervalID = setInterval(async () => {
        const { data: exportStatus } =
          await apiClient.fetchExportAuthAuditLogsTaskStatus(taskID);

        switch (exportStatus.status) {
          case "DONE": {
            clearInterval(intervalID);
            const { data: csv } =
              await apiClient.downloadExportAuthAuditLogs(taskID);
            FileSaver.saveAs(
              new Blob([csv]),
              `認証ログ_${new Date().toDateString()}.csv`,
            );

            onExportSuccess();

            break;
          }
          case "FAILED":
            clearInterval(intervalID);
            onExportFailed(
              new Error(
                "認証ログのダウンロードに失敗しました。再度実行してください。",
              ),
            );

            break;
        }
      }, checkExportStatusInterval);
    } catch (e) {
      onExportFailed(e);
    }
  }
}
